import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { first } from 'rxjs/operators';
import { ProviderLinkService } from '../../core/services';
import { ClientService } from '../../core/services/client.service';
import { Client } from '../../domain/models';
import { AuthPopupService, AuthService } from '../../features/auth/services';
import { Provider } from '../../features/provider/models';
import { BlueBackgroundDirective } from '../../shared/directives';
import { GoogleAnalyticsService } from '../../shared/service/google-analytics.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        TranslocoModule,
        RouterModule,
        MatButtonModule,
        FlexLayoutModule,
        BlueBackgroundDirective
    ]
})
export class HomeComponent implements OnInit {
    client: Client | undefined;

    steps: { icon: string; descriptionKey: string }[] = [
        {
            icon: 'step 1.svg',
            descriptionKey: 'home.step.one'
        },
        {
            icon: 'step 2.svg',
            descriptionKey: 'home.step.two'
        },
        {
            icon: 'step 3.svg',
            descriptionKey: 'home.step.three'
        },
        {
            icon: 'step 4.svg',
            descriptionKey: 'home.step.four'
        }
    ];
    hasProvider: boolean | undefined;
    hasProgram: boolean | undefined;
    introduction: string | undefined;

    /**
     * The provider named by the deep link could not be confirmed as this client's, so the
     * application must not be started against it. Closes the only entry point on this page.
     */
    providerUnavailable = false;

    constructor(
        private readonly _router: Router,
        private readonly _client: ClientService,
        private readonly _auth: AuthService,
        private readonly _authPopup: AuthPopupService,
        private readonly _route: ActivatedRoute,
        private readonly _providerLink: ProviderLinkService,
        private readonly _transloco: TranslocoService,
        private _googleAnalyticsService: GoogleAnalyticsService
    ) {}

    ngOnInit(): void {
        this._googleAnalyticsService.pageView('Home');
        this._client.latest.subscribe((client) => {
            if (!client) return;
            this.client = client;
            // if (!this.client.isEnabled) return;

            this.introduction = this.client.introductionText;
            this._providerLink.checkForParams(this._route.snapshot.paramMap);
            this.hasProvider = this._providerLink.hasProvider;
            this.hasProgram = this._providerLink.hasPrograms;
            if (this.hasProvider) {
                this._providerLink.provider$?.subscribe({
                    next: (provider) => {
                        if (provider === null || provider === undefined) {
                            this.rejectInvalidProviderLink();
                            return;
                        }
                        this.setIntroductionText(provider);
                        this._providerLink.cleanProgramIds(provider);
                    },
                    error: () => this.blockUnverifiedProvider()
                });

                // Remove the first step, and reset the icons
                this.steps = [
                    {
                        icon: 'step 2.svg',
                        descriptionKey: 'home.step.two'
                    },
                    {
                        icon: 'step 3.svg',
                        descriptionKey: 'home.step.three'
                    },
                    {
                        icon: 'step 4.svg',
                        descriptionKey: 'home.step.four'
                    }
                ];
            }
        });
    }
    navigateToProgram() {
        this._router.navigateByUrl(`${this.client?.clientCode}/program`);
    }

    startApplication(): void {
        // Belt and braces: the button is disabled in the template, but nothing else stands between
        // an unverified provider and an application built around it.
        if (this.providerUnavailable) return;

        if (this._auth.isSignedIn)
            this._router.navigateByUrl(
                `${this.client?.clientCode}/application`
            );
        else this._authPopup.showAccountCreation();
    }

    /**
     * The deep link named a provider that is not this client's.
     *
     * The lookup is by clientId + providerId and filters on visibility, so an empty result is an
     * answer, not a failure: a mistyped id, a retired provider, or another client's id
     * (`/ga/providers/400` names a Tennessee provider). The student is put on the statewide home
     * page, where they can search for a provider — with nothing said on screen, because there is no
     * version of "that link was not valid" that means anything to someone who was handed a URL.
     *
     * The discard is the part that must not be dropped. It reads like the redirect this page has
     * always done, but it is no longer the redirect that clears the selection: `checkForParams` used
     * to wipe the staged provider ids on any route without a `providerId`, and it deliberately no
     * longer does. Without `discardInvalidLink()` the rejected id would survive the trip to /home,
     * and the student would carry it into sign-up only to have the API refuse it — the confusing
     * failure, two pages later, instead of a quiet redirect (LA-22939).
     */
    private rejectInvalidProviderLink(): void {
        this._providerLink.discardInvalidLink();
        this.hasProvider = false;

        this._router.navigate([`${runtimeConfig.clientCode}/home`]);
    }

    /**
     * The lookup failed rather than answering, so whether the provider is this client's is unknown.
     *
     * The staged selection is kept — a reload can still confirm it, and throwing away a legitimate
     * student's agency over a transient API failure is the mistake this pair of paths exists to
     * separate. But the application cannot be started against a provider that has not been
     * confirmed, so the entry point is closed until the page is reloaded. HttpErrorInterceptor has
     * already reported the failure itself; the template explains what it means here.
     */
    private blockUnverifiedProvider(): void {
        this.providerUnavailable = true;
    }

    private setIntroductionText(provider: Provider | undefined): void {
        if (provider) {
            this._transloco
                .selectTranslate('home.provider-introduction')
                .pipe(first())
                .subscribe(
                    (message) =>
                        (this.introduction = `${message} ${provider.name}.`)
                );
        }
    }
}
